// Board-side rules for /admin-portal/therapist-schedules: turning the upstream
// calendar into per-day cells, staying inside the endpoint's range limit, and
// keeping the client-side fan-out from saturating Puma.

import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import type {
	AvailabilityStatus,
	TherapistCalendarDay,
	TherapistCalendarParams,
} from "./types";

/**
 * Upstream rejects `end_date - start_date > 90` days
 * (`handler/therapist/calendar.go`), while this page's date filter is free-form.
 * A 90-day *span* — i.e. 91 dates — is the largest single request.
 */
export const CALENDAR_MAX_RANGE_DAYS = 90;

/**
 * Cap on calendar requests in flight at once. These fetches look client-side but
 * hit a Rails route that proxies synchronously to Go, so each one parks a Puma
 * thread for up to the proxy's 10s timeout. What bounds the risk is the number in
 * flight, not the number of therapists — so the board caps this instead of
 * capping how many therapists an admin may compare.
 */
export const CALENDAR_FANOUT_CONCURRENCY = 4;

/** Split an inclusive ISO date range into windows the calendar endpoint accepts. */
export function chunkDateRange(
	from: string,
	to: string,
	maxRangeDays = CALENDAR_MAX_RANGE_DAYS,
): TherapistCalendarParams[] {
	const start = parseISO(from);
	const end = parseISO(to);
	if (differenceInCalendarDays(end, start) < 0) return [];

	const chunks: TherapistCalendarParams[] = [];
	let cursor = start;
	while (differenceInCalendarDays(end, cursor) > maxRangeDays) {
		const chunkEnd = addDays(cursor, maxRangeDays);
		chunks.push({
			start_date: format(cursor, "yyyy-MM-dd"),
			end_date: format(chunkEnd, "yyyy-MM-dd"),
		});
		cursor = addDays(chunkEnd, 1);
	}
	chunks.push({
		start_date: format(cursor, "yyyy-MM-dd"),
		end_date: format(end, "yyyy-MM-dd"),
	});
	return chunks;
}

/** What one day-header cell on the board shows. */
export type DayAvailability = {
	status: AvailabilityStatus;
	/** Distinct appointments — the headline number, see `bookedSlots`. */
	visitCount: number;
	totalSlots: number;
	availableSlots: number;
	/**
	 * Candidate start-times blocked, NOT visits: one visit spanning
	 * duration+buffer covers several candidates. Never shown in the cell for that
	 * reason — it needs a sentence of context, which only the detail panel has
	 * room for.
	 */
	bookedSlots: number;
	workingHours?: string;
	reason?: string;
	isOutsideContract: boolean;
};

/** Index calendar days (possibly from several chunked responses) by ISO date. */
export function mapCalendarDays(
	days: readonly TherapistCalendarDay[],
): Record<string, DayAvailability> {
	const byDate: Record<string, DayAvailability> = {};
	for (const day of days) {
		byDate[day.date] = {
			status: day.availability_status,
			visitCount: day.visit_count ?? 0,
			totalSlots: day.total_slots,
			availableSlots: day.available_slots,
			bookedSlots: day.booked_slots,
			workingHours: day.working_hours,
			reason: day.reason,
			isOutsideContract: day.is_outside_contract ?? false,
		};
	}
	return byDate;
}

/**
 * Render an upstream "HH:mm" clock label in the active locale — 24-hour for
 * Indonesian, 12-hour with AM/PM otherwise, matching the convention the rest of
 * this page already uses for appointment times.
 *
 * Deliberately a string transform rather than date-fns: these are wall-clock
 * labels in the therapist's own timezone (GMT+7), not instants. Routing them
 * through `Date` would let the viewer's timezone shift the digits, so a schedule
 * that says 09:00 could render as 08:00 for an admin sitting in another zone —
 * silently changing what the therapist's own schedule claims.
 *
 * Unparseable input is passed through untouched: showing upstream's raw label
 * beats showing "NaN:NaN".
 */
export function formatClock(time: string, localeCode: string): string {
	const [rawHour, minute] = time.split(":");
	const hour = Number(rawHour);
	if (
		minute === undefined ||
		!Number.isInteger(hour) ||
		hour < 0 ||
		hour > 23
	) {
		return time;
	}
	if (localeCode.startsWith("id")) {
		return `${String(hour).padStart(2, "0")}:${minute}`;
	}
	const half = hour < 12 ? "AM" : "PM";
	const twelve = hour % 12 === 0 ? 12 : hour % 12;
	return `${String(twelve).padStart(2, "0")}:${minute} ${half}`;
}

/** Same, for upstream's `working_hours` ("09:00-18:00"). */
export function formatClockRange(range: string, localeCode: string): string {
	const parts = range.split("-");
	if (parts.length !== 2) return range;
	return `${formatClock(parts[0].trim(), localeCode)}–${formatClock(
		parts[1].trim(),
		localeCode,
	)}`;
}

/**
 * The day cell's load meter: visits drawn against the therapist's real daily cap.
 *
 * `max_daily_appointments` is not a display convention — it is the ceiling the
 * booking engine enforces (`dayAtCap` in manager/therapist/calendar.go closes a
 * day the moment visits reach it). That makes "2 of 3" a fact about bookability
 * rather than a decorated count.
 *
 * Returns null when the therapist has no cap configured (upstream sends 0, which
 * means "uncapped", not "zero allowed"). The cell then shows the bare count —
 * inventing a denominator would be fabricated data.
 *
 * `segments` can exceed `cap`: an over-cap day is drawn overflowing rather than
 * clamped, because a day past its ceiling is exactly what an admin needs to see.
 */
export type DayLoad = { filled: number; cap: number; segments: number };

export function dayLoad(
	visitCount: number,
	maxDailyAppointments: number | undefined,
): DayLoad | null {
	if (!maxDailyAppointments || maxDailyAppointments <= 0) return null;
	return {
		filled: visitCount,
		cap: maxDailyAppointments,
		segments: Math.max(maxDailyAppointments, visitCount),
	};
}

/**
 * Whether a day earns a row on the board.
 *
 * The upstream calendar returns every date in the range — and under
 * `bypass_contract_check` it no longer even clamps to the contract window — so a
 * month of Sundays would otherwise fill the column with "Not working". The Ruby
 * path this replaces dropped those days too (`next if slots.blank?`), while
 * keeping days blocked by an adjustment, which carry a reason worth reading.
 *
 * A day with appointments is always shown, whatever the status says: the visit
 * list is the point of the board.
 */
export function isDayWorthShowing(availability: DayAvailability): boolean {
	return availability.status !== "NOT_WORKING";
}

/**
 * Statuses the upstream calendar leaves out of `visit_count` but Rails still
 * sends down. Only NO_SHOW can actually reach here: ON HOLD, UNSCHEDULED and
 * PENDING THERAPIST ASSIGNMENT all lack a therapist or a date, so Go's query
 * never matches them and Rails' `invalid_statuses` already drops them.
 *
 * Applied to the card's number only — the appointment block stays on the board so
 * the no-show is still visible.
 */
const NOT_A_VISIT_UPSTREAM = new Set(["NO_SHOW"]);

export function countsAsVisit(status: string): boolean {
	return !NOT_A_VISIT_UPSTREAM.has(status);
}

/**
 * Run thunks with at most `limit` outstanding. Settles rather than rejects: one
 * therapist's calendar failing must not blank the whole board.
 */
export async function runWithConcurrency<T>(
	tasks: readonly (() => Promise<T>)[],
	limit = CALENDAR_FANOUT_CONCURRENCY,
): Promise<PromiseSettledResult<T>[]> {
	const results = new Array<PromiseSettledResult<T>>(tasks.length);
	let next = 0;

	const worker = async () => {
		while (next < tasks.length) {
			const index = next++;
			try {
				results[index] = { status: "fulfilled", value: await tasks[index]() };
			} catch (reason) {
				results[index] = { status: "rejected", reason };
			}
		}
	};

	await Promise.all(
		Array.from({ length: Math.min(Math.max(limit, 1), tasks.length) }, worker),
	);
	return results;
}
